/**
 * Shared photo-upload pipeline used by Memories (family audience) and
 * Generations (circle audience). Pure async function — callers wire it
 * into React Query themselves so they can choose their own optimistic
 * update + invalidation strategy.
 *
 * Pipeline:
 *  1. SHA-256 the original bytes (WebCrypto). Send the hex digest to
 *     /photos/presign — the server uses it to dedup against existing
 *     photos in the family.
 *  2. If the server reports an existing match, we're done: no encode,
 *     no R2 PUTs, no finalize. For circle audience, the server already
 *     linked the existing photo into the requested circle.
 *  3. Otherwise: parse EXIF, decode the bitmap once, encode two WebP
 *     variants (2400 large, 400 thumb), PUT both to R2 directly, then
 *     POST /photos/finalize with audience + hash + EXIF metadata.
 *
 * Returns a status object so callers can distinguish "uploaded a fresh
 * row" from "already had it; nothing to do" if they want to surface
 * that to the user.
 */
import exifr from 'exifr';
import { api } from './api';

interface PresignUrls {
  photoId: string;
  r2Key: string;
  largeKey: string;
  largeUrl: string;
  thumbKey: string;
  thumbUrl: string;
}

interface PresignExisting {
  existing: {
    photoId: string;
    r2Key: string;
    /** True if the photo was already linked to the requested circle. */
    alreadyShared: boolean;
  };
}

type PresignResponse = PresignUrls | PresignExisting;

export type PhotoAudience = { type: 'family' } | { type: 'circle'; circleId: string };

export interface UploadResult {
  /** 'uploaded' = brand-new row. 'deduped' = server matched the hash
   *  and either no-op'd (family) or linked the existing row into the
   *  requested circle (circle audience). */
  kind: 'uploaded' | 'deduped';
  /** True only when audience is circle AND the existing photo was
   *  already shared with that circle. UI can surface "Already in this
   *  Generation" instead of "Added." */
  alreadyShared: boolean;
  /** The photo's stable row id, whether freshly uploaded or deduped.
   *  Callers that embed the photo inline (e.g. Sermon Notes) use it to
   *  build the `/api/photos/<id>/large` reference. */
  photoId: string;
}

export async function uploadPhoto(
  file: File,
  audience: PhotoAudience = { type: 'family' },
): Promise<UploadResult> {
  const hash = await sha256Hex(file);

  const presign = await api.post<PresignResponse>('/photos/presign', {
    mimeType: 'image/webp',
    // The size here is for the original file; the server only uses it
    // for the 50 MB sanity cap. We swap in the encoded large size on
    // the normal path because that's what actually lands in R2.
    sizeBytes: Math.min(file.size, 49_000_000),
    hash,
    audience,
  });

  if ('existing' in presign) {
    return {
      kind: 'deduped',
      alreadyShared: presign.existing.alreadyShared,
      photoId: presign.existing.photoId,
    };
  }

  const exif = await exifr.parse(file).catch(() => ({}));
  const takenAt = exif?.DateTimeOriginal
    ? new Date(exif.DateTimeOriginal).getTime()
    : Date.now();

  // Decode the file ONCE, then share the bitmap with both encodes.
  const bitmap = await createImageBitmap(file);
  const [largeBlob, thumbBlob] = await Promise.all([
    encodeWebp(bitmap, 2400, 0.92),
    encodeWebp(bitmap, 400, 0.8),
  ]);
  bitmap.close?.();

  const directPut = (url: string, blob: Blob) =>
    fetch(url, {
      method: 'PUT',
      headers: { 'content-type': 'image/webp' },
      body: blob,
    }).then((res) => {
      if (!res.ok) throw new Error(`R2 PUT failed (${res.status})`);
    });
  await Promise.all([
    directPut(presign.largeUrl, largeBlob),
    directPut(presign.thumbUrl, thumbBlob),
  ]);

  await api.post('/photos/finalize', {
    r2Key: presign.largeKey,
    mimeType: 'image/webp',
    takenAt,
    lat: exif?.latitude ?? null,
    lng: exif?.longitude ?? null,
    width: null,
    height: null,
    audience,
    hash,
  });
  return { kind: 'uploaded', alreadyShared: false, photoId: presign.photoId };
}

/** SHA-256 of the entire file, lowercase hex. */
async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

async function encodeWebp(
  bitmap: ImageBitmap,
  maxEdge: number,
  quality: number,
): Promise<Blob> {
  const scale = Math.min(maxEdge / Math.max(bitmap.width, bitmap.height), 1);
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/webp',
      quality,
    );
  });
}
